import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  confirmPhoneRepair,
  getCurrentUser,
  loginUser,
  logoutUser,
  markPrizePopupShown,
} from "../services/authService";
import { clearCsrfToken, setCsrfToken } from "../lib/api";

const AuthContext = createContext(null);

function normalizePrizePopup(prizePopup) {
  return {
    shouldShow: Boolean(prizePopup?.shouldShow),
  };
}

function normalizePhoneRepair(phoneRepair) {
  return {
    shouldShow: Boolean(phoneRepair?.shouldShow),
    field: phoneRepair?.field ?? null,
    label: phoneRepair?.label ?? "",
    displayValue: phoneRepair?.displayValue ?? "",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [prizePopup, setPrizePopup] = useState({ shouldShow: false });
  const [phoneRepair, setPhoneRepair] = useState({
    shouldShow: false,
    field: null,
    label: "",
    displayValue: "",
  });
  const [status, setStatus] = useState("loading");
  const hasLoadedRef = useRef(false);

  const refreshSession = async () => {
    try {
      const response = await getCurrentUser();
      console.log("[PrizePopup][frontend][me]", response?.prizePopup ?? null);
      if (response?.user) {
        setUser(response.user);
        setPrizePopup(normalizePrizePopup(response.prizePopup));
        setPhoneRepair(normalizePhoneRepair(response.phoneRepair));
        setCsrfToken(response.csrfToken);
        setStatus("authenticated");
        return response.user;
      }

      setUser(null);
      setPrizePopup({ shouldShow: false });
      setPhoneRepair({ shouldShow: false, field: null, label: "", displayValue: "" });
      clearCsrfToken();
      setStatus("anonymous");
      return null;
    } catch (error) {
      setUser(null);
      setPrizePopup({ shouldShow: false });
      setPhoneRepair({ shouldShow: false, field: null, label: "", displayValue: "" });
      clearCsrfToken();
      setStatus("anonymous");
      return null;
    }
  };

  useEffect(() => {
    if (hasLoadedRef.current) {
      return;
    }

    hasLoadedRef.current = true;
    void refreshSession();
  }, []);

  const signIn = async (credentials) => {
    const response = await loginUser(credentials);
    console.log("[PrizePopup][frontend][login]", response?.prizePopup ?? null);
    setUser(response.user);
    setPrizePopup(normalizePrizePopup(response.prizePopup));
    setPhoneRepair(normalizePhoneRepair(response.phoneRepair));
    setCsrfToken(response.csrfToken);
    setStatus("authenticated");
    return response;
  };

  const completePhoneRepair = async (payload) => {
    const response = await confirmPhoneRepair(payload);
    setUser(response.user);
    setPrizePopup(normalizePrizePopup(response.prizePopup));
    setPhoneRepair(normalizePhoneRepair(response.phoneRepair));
    setCsrfToken(response.csrfToken);
    setStatus("authenticated");
    return response;
  };

  const acknowledgePrizePopupShown = async () => {
    console.log("[PrizePopup][frontend][ack] sending request");
    await markPrizePopupShown();
    console.log("[PrizePopup][frontend][ack] request completed");
    setPrizePopup({ shouldShow: false });
  };

  const signOut = async () => {
    try {
      await logoutUser();
    } catch {
      // If the backend is unavailable, still clear the local auth state.
    } finally {
      setUser(null);
      setPrizePopup({ shouldShow: false });
      setPhoneRepair({ shouldShow: false, field: null, label: "", displayValue: "" });
      clearCsrfToken();
      setStatus("anonymous");
    }
  };

  const value = {
    user,
    prizePopup,
    phoneRepair,
    status,
    isLoading: status === "loading",
    isAuthenticated: status === "authenticated",
    refreshSession,
    signIn,
    completePhoneRepair,
    acknowledgePrizePopupShown,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
